import { ProspectorBusinessQuery } from "generated/graphql";

import { OnboardingProps } from "../models";
import { CMSType } from "./determineCMS";

const CMS_TYPES: readonly CMSType[] = ["wix", "wordpress", "squarespace", "joomla", "godaddy"];

/**
 * Prospector reports the CMS as a human label ("WordPress", "GoDaddy Website Builder").
 * Normalize to the CMSType union the app uses, dropping platforms we don't model.
 */
function normalizeCms(cms?: string | null): CMSType | undefined {
    if (!cms) {
        return undefined;
    }
    const first = cms.trim().toLowerCase().split(/\s+/)[0];
    return (CMS_TYPES as readonly string[]).includes(first) ? (first as CMSType) : undefined;
}

/**
 * The branding fields prospector must supply for us to skip the website crawl entirely.
 * Miss any one of these and we fall back to the (slower, failure-prone) crawl.
 */
export const PROSPECTOR_BRANDING_FIELDS: readonly (keyof OnboardingProps)[] = [
    "primaryColor",
    "secondaryColor",
    "logo",
    "businessDescription",
];

/**
 * True when onboarding props already carry every required branding field (from prospector),
 * so the finalize step can theme the widgets without waiting on a website crawl.
 */
export function hasCompleteProspectorBranding(props?: OnboardingProps): boolean {
    if (!props) {
        return false;
    }
    return PROSPECTOR_BRANDING_FIELDS.every((field) => Boolean(props[field]));
}

/**
 * The prospector business envelope, derived from the generated GraphQL types so it
 * stays in sync with the `prospectorBusiness` query / backend schema. Regenerating
 * with `yarn codegen` after a schema change surfaces any drift as a type error here
 * rather than silently producing blank prefill.
 */
export type ProspectorBusinessEnvelope = NonNullable<ProspectorBusinessQuery["public"]["business"]>;

/**
 * Maps a prospector business envelope to OnboardingProps, seeding only fields
 * that are not already set in the existing props.
 */
export function mapProspectorToOnboardingProps(
    envelope: ProspectorBusinessEnvelope | null | undefined,
    existingProps: OnboardingProps
): OnboardingProps {
    const business = envelope?.business;
    if (!business) {
        return existingProps;
    }

    const result: OnboardingProps = { ...existingProps };

    if (!result.name && business.name) {
        result.name = business.name;
    }

    if (!result.website && business.websiteUrl) {
        result.website = business.websiteUrl;
    }

    if (!result.leadsContactPhone && business.phone) {
        result.leadsContactPhone = business.phone;
    }

    if (!result.logo && business.logoUrl) {
        result.logo = business.logoUrl;
    }

    if (!result.searchAddress && business.city && business.state) {
        result.searchAddress = `${business.city}, ${business.state}`;
    }

    // Carry prospector's Google Place ID through as the seed `place`. The find/confirm step
    // fetches the full details from this ID directly, skipping the fuzzy name+city text search
    // that otherwise returns multiple candidates and forces a manual location disambiguation.
    if (!result.place && business.googlePlaceId) {
        result.place = { placeId: business.googlePlaceId };
    }

    // Branding straight from prospector — the same data the website crawl produces, but
    // already available here. When all of it is present the finalize step skips the crawl.
    if (!result.primaryColor && business.primaryColor) {
        result.primaryColor = business.primaryColor;
    }

    if (!result.secondaryColor && business.secondaryColor) {
        result.secondaryColor = business.secondaryColor;
    }

    if (!result.businessDescription && business.description) {
        result.businessDescription = business.description;
    }

    if (!result.cms) {
        const cms = normalizeCms(business.cms);
        if (cms) {
            result.cms = cms;
        }
    }

    return result;
}
